import React, { useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { formatCurrency } from "../../utils/helpers";
import Button from "../ui/Button";

const AdPerformanceTable = ({
  data,
  isLoading,
  currentPage,
  setCurrentPage,
  rowsPerPage,
  totalRows,
  loadingProgress,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: "ad_spend",
    direction: "descending",
  });

  const sortedData = React.useMemo(() => {
    let sortableItems = [...(data || [])];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    if (sortConfig.direction === "ascending")
      return <ArrowUp className="w-4 h-4 ml-1" />;
    return <ArrowDown className="w-4 h-4 ml-1" />;
  };

  const headers = [
    { key: "adName", label: "Ad Name" },
    { key: "adSpend", label: "Ad Spend" },
    { key: "revenue", label: "Revenue" },
    { key: "roas", label: "ROAS" },
    { key: "purchases", label: "Purchases" },
    { key: "commission", label: "Commission" },
  ];

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-white">
          <thead>
            <tr className="border-b border-white/10">
              {headers.map((header) => (
                <th
                  key={header.key}
                  className="p-4 text-left text-sm font-semibold tracking-wider"
                >
                  <button
                    onClick={() => requestSort(header.key)}
                    className="flex items-center"
                  >
                    {header.label}
                    {getSortIcon(header.key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={headers.length} className="text-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#B8460E]"></div>
                    {/* Main Loading Message */}
                    <div className="text-white text-lg font-medium">
                      {loadingProgress && loadingProgress.isComplete
                        ? `✅ Loading Complete! All ${loadingProgress.total.toLocaleString()} ads loaded`
                        : "Loading ALL ad performance data..."}
                    </div>

                    {loadingProgress && (
                      <div className="space-y-3 w-full max-w-md">
                        {/* Progress Bar */}
                        {loadingProgress.percent > 0 && (
                          <div className="w-full bg-gray-700 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-[#B8460E] to-[#FF6B35] h-3 rounded-full transition-all duration-300 flex items-center justify-center"
                              style={{
                                width: `${Math.min(loadingProgress.percent, 100)}%`,
                              }}
                            >
                              {loadingProgress.percent >= 20 && (
                                <span className="text-white text-xs font-semibold">
                                  {loadingProgress.percent}%
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Detailed Progress Message */}
                        <div className="text-gray-300 text-sm text-center">
                          {loadingProgress.isComplete ? (
                            <span className="text-green-400 font-medium">
                              🎉 Successfully loaded{" "}
                              {loadingProgress.total.toLocaleString()} total ad
                              instances
                            </span>
                          ) : loadingProgress.total > 0 &&
                            loadingProgress.estimated > 0 ? (
                            <span>
                              Loaded{" "}
                              <span className="text-[#B8460E] font-semibold">
                                {loadingProgress.total.toLocaleString()}
                              </span>{" "}
                              ads of ~
                              <span className="text-blue-400">
                                {loadingProgress.estimated.toLocaleString()}
                              </span>{" "}
                              -{" "}
                              <span className="text-green-400 font-semibold">
                                {loadingProgress.percent}%
                              </span>{" "}
                              complete
                            </span>
                          ) : loadingProgress.total > 0 ? (
                            <span>
                              Loaded{" "}
                              <span className="text-[#B8460E] font-semibold">
                                {loadingProgress.total.toLocaleString()}
                              </span>{" "}
                              ads...
                            </span>
                          ) : (
                            "Initializing batch loading..."
                          )}
                        </div>

                        {/* Technical Details */}
                        {loadingProgress.batch > 0 &&
                          !loadingProgress.isComplete && (
                            <div className="text-gray-400 text-xs space-y-1 text-center">
                              <div>
                                Processing batch{" "}
                                <span className="text-[#B8460E]">
                                  {loadingProgress.batch}
                                </span>
                                {loadingProgress.loadTime > 0 && (
                                  <span className="ml-2">
                                    ({loadingProgress.loadTime}ms)
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}

                    <div className="text-gray-400 text-sm">
                      This may take 1-2 minutes for very large datasets
                    </div>
                  </div>
                </td>
              </tr>
            ) : sortedData.length > 0 ? (
              sortedData.map((row, index) => (
                <tr
                  key={index}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="p-4">{row.adName}</td>
                  <td className="p-4">{formatCurrency(row.adSpend)}</td>
                  <td className="p-4">{formatCurrency(row.revenue)}</td>
                  <td className="p-4">{row.roas.toFixed(2)}</td>
                  <td className="p-4">{row.purchases}</td>
                  <td className="p-4">{formatCurrency(row.commission)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className="text-center py-8 text-gray-400"
                >
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-400">
        <div>
          Showing {(currentPage - 1) * rowsPerPage + 1}-
          {Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            variant="outline"
            className="p-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            variant="outline"
            className="p-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdPerformanceTable;
